import React, { useState, useEffect, useMemo } from "react";
import { projects as projectsApi, reservationsScheduler } from "../services/api";
import toast from "react-hot-toast";
import { FiCalendar, FiFilter, FiDownload, FiFileText, FiEdit2, FiSave, FiX, FiClock, FiMessageCircle, FiSettings } from "react-icons/fi"; /* v3.4.448 FiSettings */
import WhatsAppChatModal from "../components/WhatsAppChatModal";
import ReservationNotesCell from "../components/ReservationNotesCell";
import GenericHeaderMenu from "../components/GenericHeaderMenu"; // v3.3.999 — هيدر الحجوزات القياسي
import { useAuth } from "../context/AuthContext"; // v3.4.486
import { useNavigate } from "react-router-dom"; // v3.4.590 — زر «إنشاء عقد»

const fmt = (n) => (n == null || isNaN(n)) ? "0" : Math.round(Number(n)).toLocaleString("en-US");
// v3.4.477 — شهر موعد التقفيل بالتوقيت المحلي (التاريخ يصل كطابع UTC)
const ymLocal = (v) => { if (!v) return ""; const d = new Date(v); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0"); };
const ymNext = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + 1); return ymLocal(d); }; // v3.4.485 — الشهر القادم
const pct = (n) => (n == null || isNaN(n)) ? "0%" : `${Math.round(Number(n))}%`;

// v3.4.473 — خلية «المبلغ المتوقع سداده»: ضغطة للتعديل، Enter/خروج للحفظ، Esc للإلغاء
function ExpectedAmountCell({ u, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const has = u.expected_payment_amount != null;
  const start = () => { setVal(has ? String(Math.round(u.expected_payment_amount)) : ""); setEditing(true); };
  const save = async () => {
    const clean = String(val).replace(/[,\s]/g, "").replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));
    const next = clean === "" ? null : Number(clean);
    if (next !== null && (!isFinite(next) || next < 0)) { toast.error("أدخل مبلغًا صحيحًا"); return; }
    if ((next ?? null) === (has ? Math.round(u.expected_payment_amount) : null)) { setEditing(false); return; }
    setBusy(true);
    try { await reservationsScheduler.updateExpectedAmount(u.reservation_id, next); onSaved(u.reservation_id, next); toast.success(next === null ? "أُزيلت الدفعة الأولى المتوقعة" : "✅ حُفظت الدفعة الأولى المتوقعة"); setEditing(false); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(false);
  };
  if (editing) return (
    <input autoFocus value={val} disabled={busy} inputMode="decimal" placeholder="0"
      onChange={e => setVal(e.target.value)} onBlur={save}
      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditing(false); }}
      style={{ width: 110, padding: "5px 8px", border: "1.5px solid #7c3aed", borderRadius: 7, fontFamily: "monospace", fontSize: 12.5, fontWeight: 800, textAlign: "center", outline: "none", boxShadow: "0 0 0 3px rgba(124,58,237,.12)" }} />
  );
  return has ? (
    <button type="button" onClick={start} title="اضغط للتعديل"
      style={{ background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe", borderRadius: 7, padding: "3px 10px", fontFamily: "monospace", fontWeight: 900, fontSize: 12.5, cursor: "pointer" }}>{fmt(u.expected_payment_amount)}</button>
  ) : (
    <button type="button" onClick={start} title="أدخل الدفعة الأولى المتوقعة"
      style={{ background: "transparent", color: "#cbd5e1", border: "1px dashed #e2e8f0", borderRadius: 7, padding: "3px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>+ إدخال</button>
  );
}

export default function ReservationsSchedulerPage() {
  // v3.4.486 — صلاحية لكل بطاقة إحصاء (الأدمن يرى الكل، وفي وضع المعاينة تُطبَّق صلاحيات البروفايل)
  const { user } = useAuth();
  const isAdminS = !!user && (user.role === "admin" || user.is_admin) && !user.impersonating;
  const sc = Object.fromEntries(["total", "value", "expected_month", "expected_next", "overdue", "today", "week", "no_date"].map(k => [k, isAdminS || !!user?.["can_view_sched_card_" + k]]));
  const navigate = useNavigate();
  const canContracts = isAdminS || !!user?.can_view_contracts; // v3.4.590
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [data, setData] = useState({ units: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ closing_date: "", closing_time: "", closing_notes: "" });
  const [filter, setFilter] = useState("all"); // all | overdue | today | this_week | no_date
  // v3.3.366: WhatsApp chat modal
  const [chatTarget, setChatTarget] = useState(null);  // {phone, name, clientId}
  // v3.3.371: search + sort
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [colFilters, setColFilters] = useState({}); // v3.3.999 — فلاتر قيم لكل عمود (نمط إكسل المتسلسل)

  useEffect(() => {
    projectsApi.list().then(r => setProjects(r.data.projects || [])).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    reservationsScheduler.list({}) // v3.4.462 — كل المشاريع مرة واحدة؛ فلتر المشروع على الصفحة (أعداد لكل مشروع)
      .then(r => setData(r.data))
      .catch(() => toast.error("فشل تحميل البيانات"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // v3.4.462 — مرة واحدة

  // v3.3.603 — المدة منذ تاريخ الحجز (محسوبة قبل الفلترة حتى تعمل عليها الفلاتر والفرز)
  // v3.4.462 — صفوف المشروع المختار + البطاقات محسوبة على الصفحة بنفس قواعد الخادم
  const projRows = useMemo(() => (projectId ? data.units.filter(u => String(u.project_id) === String(projectId)) : data.units), [data.units, projectId]);
  const summary = useMemo(() => {
    const t0 = new Date(); t0.setHours(0, 0, 0, 0);
    const dd = (u) => { if (!u.closing_date) return null; const d = new Date(u.closing_date); d.setHours(0, 0, 0, 0); return Math.round((d - t0) / 86400000); };
    return { total: projRows.length, overdue: projRows.filter(u => { const x = dd(u); return x !== null && x < 0; }).length, today: projRows.filter(u => dd(u) === 0).length,
      this_week: projRows.filter(u => { const x = dd(u); return x !== null && x > 0 && x <= 7; }).length, no_date: projRows.filter(u => !u.closing_date).length };
  }, [projRows]);
  const projectPills = useMemo(() => { const m = new Map(); for (const u of data.units) { const k = String(u.project_id); if (!m.has(k)) m.set(k, { id: u.project_id, name: u.project_name || "—", n: 0 }); m.get(k).n++; } return [...m.values()].sort((a, b) => b.n - a.n || String(a.name).localeCompare(String(b.name), "ar")); }, [data.units]);
  const baseRows = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return projRows.map(u => {
      let days = null;
      if (u.reservation_date) {
        const d = new Date(String(u.reservation_date).slice(0, 10));
        if (!isNaN(d.getTime())) { d.setHours(0, 0, 0, 0); days = Math.floor((today - d) / 86400000); }
      }
      return { ...u, days_since_reservation: days };
    });
  }, [projRows]);

  // v3.3.999 — نمط إكسل المتسلسل (كما في التحصيلات والديون v3.3.994): سلسلة الفلاتر كاملة مع استثناء
  // فلتر واحد (exceptKey) — قوائم القيم تعرض المتبقي الظاهر فقط، وفلتر العمود نفسه يبقى قابلاً للتعديل
  const getColVal = (u, k) => (k === "unit_label" ? (u.unit_label || u.unit_number || "") : (u[k] ?? ""));
  const applyFilters = (arr, exceptKey) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const q = search.trim().toLowerCase();
    return arr.filter(u => {
      // فلتر نطاق المواعيد
      if (exceptKey !== "range") {
        if (filter === "no_date" && u.closing_date) return false;
        if (filter === "expected_month" && !(ymLocal(u.closing_date) === ymLocal(new Date()) && u.expected_payment_amount > 0)) return false; // v3.4.477
        if (filter === "expected_next" && !(ymLocal(u.closing_date) === ymNext() && u.expected_payment_amount > 0)) return false; // v3.4.485
        if (filter !== "all" && filter !== "no_date" && filter !== "expected_month" && filter !== "expected_next") {
          if (!u.closing_date) return false;
          const d = new Date(u.closing_date); d.setHours(0, 0, 0, 0);
          const diff = Math.round((d - today) / 86400000);
          if (filter === "overdue" && diff >= 0) return false;
          if (filter === "today" && diff !== 0) return false;
          if (filter === "this_week" && (diff <= 0 || diff > 7)) return false;
        }
      }
      // فلاتر الأعمدة
      if (!Object.entries(colFilters).every(([k, vals]) => k === exceptKey || !vals?.length || vals.includes(String(getColVal(u, k))))) return false;
      // v3.3.371 — بحث عام في كل الأعمدة
      if (q) {
        const haystack = [
          u.project_name, u.unit_label, u.unit_number, String(u.reservation_id ?? ""), u.client_name, u.client_phone,
          u.closing_date, u.closing_notes, u.closing_status,
          String(u.effective_price ?? ""), String(u.deposit_paid ?? ""), /* v3.4.462 */ String(u.expected_payment_amount ?? ""), /* v3.4.473 */
          u.reservation_date,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  };
  const distinctOf = (k) => [...new Set(applyFilters(baseRows, k).map(u => String(getColVal(u, k))).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ar"));
  const filtered = useMemo(() => {
    let arr = applyFilters(baseRows, null);
    // v3.3.371 — sort
    if (sort.key) {
      const dir = sort.dir === "asc" ? 1 : -1;
      arr = [...arr].sort((a, b) => {
        const va = a[sort.key]; const vb = b[sort.key];
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        const na = Number(va); const nb = Number(vb);
        if (!isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
        return String(va).localeCompare(String(vb), "ar") * dir;
      });
    }
    return arr;
  }, [baseRows, filter, search, sort, colFilters]); // eslint-disable-line

  // v3.3.999 — رأس عمود بنمط جدول الحجوزات
  const SortTh = ({ label, k, align = "center", color = "#475569", filter: filterable = false }) => (
    k ? (
      <GenericHeaderMenu label={label} colKey={k} sortBy={sort.key} sortDir={sort.dir}
        onSort={(key, dir) => setSort({ key, dir })}
        values={filterable ? distinctOf(k) : null}
        activeFilter={colFilters[k] || []}
        onFilter={(key, vals) => setColFilters(f => ({ ...f, [key]: vals }))}
        align={align} color={color} />
    ) : (
      <th style={{ padding: "10px 8px", textAlign: align, fontWeight: 700, color, whiteSpace: "nowrap" }}>{label}</th>
    )
  );

  const startEdit = (u) => {
    setEditingId(u.reservation_id);
    setEditForm({
      closing_date: u.closing_date ? u.closing_date.slice(0, 10) : "",
      closing_time: u.closing_time ? u.closing_time.slice(0, 5) : "",
      closing_notes: u.closing_notes || "",
    });
  };

  const saveEdit = (resId) => {
    reservationsScheduler.updateClosing(resId, editForm)
      .then(() => {
        toast.success("✅ تم حفظ الموعد");
        setEditingId(null);
        load();
      })
      .catch(() => toast.error("فشل الحفظ"));
  };

  const closingBadge = (u) => {
    if (!u.closing_date) return <span style={{ color: "#94a3b8", fontSize: 11 }}>غير محدد</span>;
    const badges = {
      "متأخر":     { bg: "#fef2f2", color: "#991b1b", icon: "⚠️" },
      "اليوم":      { bg: "#fffbeb", color: "#92400e", icon: "🔥" },
      "هذا الأسبوع": { bg: "#eff6ff", color: "#1e40af", icon: "📅" },
      "قادم":       { bg: "#f0fdf4", color: "#166534", icon: "✓" },
    };
    const s = badges[u.closing_status] || badges["قادم"];
    return (
      <span style={{ background: s.bg, color: s.color, padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, display: "inline-flex", gap: 3, alignItems: "center" }}>
        {s.icon} {u.closing_status}
      </span>
    );
  };

  // Export CSV
  const exportCSV = () => {
    const headers = ["المشروع", "الوحدة", "رقم الحجز", "العميل", "سعر البيع", "العربون المدفوع", "الدفعة الأولى المتوقعة", "تاريخ الحجز", "المدة منذ الحجز (يوم)", "موعد التقفيل", "الوقت", "الحالة", "المسوق", "ملاحظات"];
    const rows = filtered.map(u => [
      u.project_name,
      u.unit_label || u.unit_number || "",
      u.reservation_id, // v3.4.368
      u.client_name || "",
      u.effective_price,
      u.deposit_paid, // v3.4.462
      u.expected_payment_amount ?? "", // v3.4.473
      u.reservation_date ? String(u.reservation_date).slice(0, 10) : "",
      u.days_since_reservation != null ? u.days_since_reservation : "",
      u.closing_date ? u.closing_date.slice(0, 10) : "",
      u.closing_time ? u.closing_time.slice(0, 5) : "",
      u.closing_status,
      (u.marketer_name || "").replace(/[\n,]/g, " "),
      (u.closing_notes || "").replace(/[\n,]/g, " "),
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `reservations_scheduler_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const win = window.open("", "_blank");
    if (!win) { toast.error("فشل فتح نافذة الطباعة"); return; }
    const rowsHtml = filtered.map(u => `
      <tr>
        <td>${u.project_name}</td>
        <td style="text-align:center;font-weight:700">${u.unit_label || u.unit_number}</td>
        <td>${u.client_name || "—"}</td>
        <td style="text-align:center"><span style="background:#3b82f6;color:#fff;font-weight:800;padding:2px 8px;border-radius:5px;-webkit-print-color-adjust:exact;print-color-adjust:exact">${fmt(u.effective_price)}</span></td>
        <td style="text-align:center;color:#b45309;font-weight:700">${u.deposit_paid > 0 ? fmt(u.deposit_paid) : "—"}</td>
        <td style="text-align:center;color:#6d28d9;font-weight:800">${u.expected_payment_amount != null ? fmt(u.expected_payment_amount) : "—"}</td>
        <td style="text-align:center;color:#64748b">${u.reservation_date ? String(u.reservation_date).slice(0, 10) : "—"}</td>
        <td style="text-align:center">${u.days_since_reservation != null ? u.days_since_reservation + " يوم" : "—"}</td>
        <td style="text-align:center;font-weight:700">${u.closing_date ? u.closing_date.slice(0, 10) : "—"}${u.closing_time ? ` 🕐 ${u.closing_time.slice(0, 5)}` : ""}</td>
        <td style="text-align:center">${u.closing_status || "—"}</td>
        <td>${u.marketer_name || "—"}</td>
        <td style="font-size:10px">${u.closing_notes || ""}</td>
      </tr>
    `).join("");
    win.document.write(`
      <!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="utf-8">
      <title>تنسيق الحجوزات</title>
      <style>
        body { font-family: system-ui, sans-serif; padding: 20px; direction: rtl; }
        h1 { font-size: 20px; color: #1e293b; }
        .subtitle { color: #64748b; font-size: 12px; margin-bottom: 20px; }
        .summary { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
        .card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; min-width: 120px; }
        .card-label { font-size: 10px; color: #94a3b8; font-weight: 600; }
        .card-value { font-size: 16px; font-weight: 800; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { padding: 8px 6px; border-bottom: 1px solid #f1f5f9; }
        th { background: #f1f5f9; text-align: right; font-weight: 700; color: #475569; }
        @media print { .no-print { display: none; } body { padding: 10px; } }
      </style></head><body>
        <h1>📅 تنسيق الحجوزات</h1>
        <div class="subtitle">${projectId ? (projects.find(p => p.id == projectId)?.name || "") : "كل المشاريع"} — ${new Date().toLocaleDateString("ar-SA")}</div>
        <div class="summary">
          <div class="card"><div class="card-label">إجمالي الحجوزات</div><div class="card-value">${fmt(summary.total)}</div></div>
          <div class="card"><div class="card-label">متأخر</div><div class="card-value" style="color:#dc2626">${fmt(summary.overdue)}</div></div>
          <div class="card"><div class="card-label">اليوم</div><div class="card-value" style="color:#f59e0b">${fmt(summary.today)}</div></div>
          <div class="card"><div class="card-label">هذا الأسبوع</div><div class="card-value" style="color:#2563eb">${fmt(summary.this_week)}</div></div>
          <div class="card"><div class="card-label">بدون موعد</div><div class="card-value" style="color:#64748b">${fmt(summary.no_date)}</div></div>
        </div>
        <div class="no-print" style="margin-bottom:12px"><button onclick="window.print()" style="padding:8px 20px;background:#16a34a;color:#fff;border:none;border-radius:6px;cursor:pointer">🖨️ طباعة / حفظ PDF</button></div>
        <table>
          <thead><tr>
            <th>المشروع</th><th>الوحدة</th><th>العميل</th><th>سعر البيع</th>
            <th>العربون المدفوع</th><th>الدفعة الأولى المتوقعة</th><th>تاريخ الحجز</th><th>المدة منذ الحجز</th><th>موعد التقفيل</th><th>الحالة</th><th>المسوق</th><th>ملاحظات</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1500, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#1e293b", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          <FiCalendar size={26} color="#2563eb" />
          تنسيق الحجوزات
        </h1>
        <p style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>
          جدولة مواعيد تقفيل الحجوزات — متابعة المواعيد والحالات قبل تحويلها إلى "مباع"
        </p>
      </div>

      {/* Summary — v3.3.321: مع بطاقة إجمالي القيمة المالية */}
      {Object.values(sc).some(Boolean) && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
        {sc.total && <StatBox label="إجمالي الحجوزات" value={fmt(summary.total)} color="#1e293b" bg="#f8fafc" onClick={() => setFilter("all")} active={filter === "all"} />}
        {sc.value && <StatBox label="💰 إجمالي القيمة" value={`${fmt(filtered.reduce((s, u) => s + (parseFloat(u.effective_price) || 0), 0))} ر.س`} color="#16a34a" bg="#f0fdf4" />}
        {/* v3.4.477 — التحصيل المتوقع هذا الشهر = مجموع «الدفعة الأولى المتوقعة» للحجوزات التي موعد تقفيلها في الشهر الحالي (ضمن المشروع المختار) */}
        {sc.expected_month && (() => { const cur = ymLocal(new Date()); const rowsM = projRows.filter(u => ymLocal(u.closing_date) === cur && u.expected_payment_amount > 0); const tot = rowsM.reduce((a, u) => a + (Number(u.expected_payment_amount) || 0), 0); const noAmt = projRows.filter(u => ymLocal(u.closing_date) === cur && !(u.expected_payment_amount > 0)).length;
          return <StatBox label="💸 التحصيل المتوقع هذا الشهر" value={fmt(tot) + " ر.س"} sub={rowsM.length + " حجز بدفعة أولى متوقعة" + (noAmt ? " · " + noAmt + " بلا مبلغ" : "")} color="#6d28d9" bg="#f5f3ff" onClick={() => setFilter(filter === "expected_month" ? "all" : "expected_month")} active={filter === "expected_month"} />; })()}
        {/* v3.4.485 — التحصيل المتوقع الشهر القادم (نفس القاعدة: موعد التقفيل في الشهر القادم × الدفعة الأولى المتوقعة) */}
        {sc.expected_next && (() => { const nx = ymNext(); const rowsN = projRows.filter(u => ymLocal(u.closing_date) === nx && u.expected_payment_amount > 0); const tot = rowsN.reduce((a, u) => a + (Number(u.expected_payment_amount) || 0), 0); const noAmt = projRows.filter(u => ymLocal(u.closing_date) === nx && !(u.expected_payment_amount > 0)).length;
          return <StatBox label="🗓️ التحصيل المتوقع الشهر القادم" value={fmt(tot) + " ر.س"} sub={rowsN.length + " حجز بدفعة أولى متوقعة" + (noAmt ? " · " + noAmt + " بلا مبلغ" : "")} color="#0369a1" bg="#f0f9ff" onClick={() => setFilter(filter === "expected_next" ? "all" : "expected_next")} active={filter === "expected_next"} />; })()}
        {sc.overdue && <StatBox label="⚠️ متأخر" value={fmt(summary.overdue)} color="#991b1b" bg="#fef2f2" onClick={() => setFilter("overdue")} active={filter === "overdue"} />}
        {sc.today && <StatBox label="🔥 اليوم" value={fmt(summary.today)} color="#92400e" bg="#fffbeb" onClick={() => setFilter("today")} active={filter === "today"} />}
        {sc.week && <StatBox label="📅 هذا الأسبوع" value={fmt(summary.this_week)} color="#1e40af" bg="#eff6ff" onClick={() => setFilter("this_week")} active={filter === "this_week"} />}
        {sc.no_date && <StatBox label="⏳ بدون موعد" value={fmt(summary.no_date)} color="#64748b" bg="#f8fafc" onClick={() => setFilter("no_date")} active={filter === "no_date"} />}
      </div>}

      {/* Filters */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <FiFilter size={14} color="#64748b" />
        {/* v3.4.462 — أزرار المشاريع (بنمط صفحة الحجوزات) مع عدد الحجوزات */}
        <div style={{ order: -1, flexBasis: "100%", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingBottom: 10, marginBottom: 2, borderBottom: "1px dashed #e2e8f0" }}>
          <span style={{ fontSize: 13, color: "#94a3b8" }}>🏘️</span>
          {[{ id: null, name: "كل المشاريع", n: data.units.length }, ...projectPills].map(p => { const on = String(projectId || "") === String(p.id || ""); return (
            <button key={p.id || "all"} type="button" onClick={() => setProjectId(p.id || null)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: on ? 800 : 600,
                border: "1.5px solid " + (on ? "#0f766e" : "#e2e8f0"), background: on ? "#f0fdfa" : "#fff", color: on ? "#0f766e" : "#475569", boxShadow: on ? "0 0 0 3px rgba(15,118,110,.08)" : "none" }}>
              {p.name}
              <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: "0 7px", background: on ? "#0f766e" : "#f1f5f9", color: on ? "#fff" : "#64748b", fontFamily: "monospace" }}>{p.n}</span>
            </button>); })}
        </div>
        {/* v3.3.371 — حقل بحث عام */}
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 بحث في كل الأعمدة..."
          style={{ padding: "7px 12px", fontSize: 12, borderRadius: 7, border: "1px solid #e2e8f0", fontFamily: "inherit", minWidth: 240 }}
        />
        {search && (
          <button onClick={() => setSearch("")}
            style={{ padding: "5px 8px", background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11 }}>
            ✕ مسح
          </button>
        )}
        <span style={{ fontSize: 11, color: "#64748b" }}>
          {filtered.length} من {data.units.length}
        </span>
        <div style={{ display: "flex", gap: 6, marginRight: "auto" }}>
          <button onClick={exportCSV} style={{ padding: "7px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <FiDownload size={12} /> Excel
          </button>
          <button onClick={exportPDF} style={{ padding: "7px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <FiFileText size={12} /> PDF
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "auto" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
        ) : filtered.length === 0 && !Object.values(colFilters).some(v => v?.length) ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>لا توجد حجوزات</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              {/* v3.3.999 — هيدر بنمط جدول الحجوزات: فلاتر قيم متسلسلة للأعمدة النصية + فرز للجميع */}
              <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                {canContracts && <SortTh label="العقد" />}{/* v3.4.590 */}
                <SortTh label="المشروع" k="project_name" align="right" filter />
                <SortTh label="الوحدة" k="unit_label" filter />
                <SortTh label="رقم الحجز" k="reservation_id" />{/* v3.4.368 */}
                <SortTh label="العميل" k="client_name" align="right" filter />
                <SortTh label="سعر البيع" k="effective_price" />{/* v3.4.462 */}
                <SortTh label="العربون المدفوع" k="deposit_paid" color="#b45309" />
                <SortTh label="الدفعة الأولى المتوقعة" k="expected_payment_amount" color="#6d28d9" />{/* v3.4.473 — v3.4.475: كانت «المبلغ المتوقع سداده» */}
                {/* v3.3.603 — تاريخ الحجز نُقل هنا قبل موعد التقفيل + عمود المدة بينهما */}
                <SortTh label="تاريخ الحجز" k="reservation_date" />
                <SortTh label="المدة منذ الحجز" k="days_since_reservation" />
                <SortTh label="موعد التقفيل" k="closing_date" color="#2563eb" />
                <SortTh label="الحالة" k="closing_status" filter />
                {/* v3.3.603 — اسم المسوق قبل الملاحظات */}
                <SortTh label="المسوق" k="marketer_name" align="right" filter />
                <SortTh label="ملاحظات" align="right" />
                <SortTh label="⚙️" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={canContracts ? 15 : 14} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>لا توجد نتائج مطابقة للفلاتر</td></tr>}
              {filtered.map(u => (
                <tr key={u.reservation_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  {canContracts && (
                    <td style={{ padding: "6px 6px", textAlign: "center", whiteSpace: "nowrap" }}>{/* v3.4.590 — رحلة العقد من الحجز */}
                      <button type="button" onClick={() => navigate(`/contracts?reservation=${u.reservation_id}`)}
                        title={u.contract_status ? "فتح عقد هذا الحجز في «العقود»" : "إنشاء عقد لهذا الحجز في «العقود»"}
                        style={u.contract_status
                          ? { background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0", borderRadius: 7, padding: "4px 10px", fontSize: 11.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }
                          : { background: "linear-gradient(135deg,#1d4ed8,#3b82f6)", color: "#fff", border: "none", borderRadius: 7, padding: "5px 10px", fontSize: 11.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 6px rgba(37,99,235,.3)" }}>
                        {u.contract_status ? (u.contract_status === "signed" ? "✍️ موقّع" : u.contract_status === "approved" ? "📄 معتمد" : "📝 مسودة") : "＋ إنشاء عقد"}
                      </button>
                    </td>
                  )}
                  <td style={{ padding: "8px 8px", color: "#1e293b" }}>{u.project_name}</td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>
                    <a href={`/projects/${u.project_id}/units/${u.unit_id}`} style={{ color: "#1e40af", textDecoration: "none", borderBottom: "1px dashed #1e40af" }}>
                      {u.unit_label || u.unit_number}
                    </a>
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>{/* v3.4.368 — رقم الحجز */}
                    <a href={`/?edit=${u.reservation_id}`} target="_blank" rel="noopener noreferrer" title="فتح الحجز" style={{ color: "#0f766e", textDecoration: "none", borderBottom: "1px dashed #0f766e" }}>#{u.reservation_id}</a>
                  </td>
                  <td style={{ padding: "8px 8px", color: "#475569" }}>
                    {u.client_name ? (
                      <a href={`/?edit=${u.reservation_id}`} target="_blank" rel="noopener noreferrer"
                         title={[
                           `📞 ${u.client_phone || "—"}`,
                           u.client_iban ? `🏦 ${u.client_iban}` : null,
                           `🆔 حجز #${u.reservation_id}`,
                           // v3.3.720 — الصفقة الأصلية عند إعادة البيع
                           u.is_resale ? `🔁 إعادة بيع${u.is_pending_resale ? " (دورة قيد الإجراءات)" : ""} — ${u.is_pending_resale ? "البائع" : "الحجز الأصلي"} #${u.original_reservation_id} (${u.original_client_name || "—"})` : null,
                         ].filter(Boolean).join("\n")}
                         style={{ color: "#475569", textDecoration: "none", borderBottom: "1px dashed #94a3b8", cursor: "help" }}>
                        {u.client_name}
                      </a>
                    ) : "—"}
                    {/* v3.3.720 — شارة إعادة البيع بجانب اسم المشتري الحالي */}
                    {u.is_resale && (
                      <span style={{ background: "#fef3c7", color: "#b45309", border: "1px solid #fde047",
                        borderRadius: 8, padding: "0px 6px", fontSize: 9, fontWeight: 800, marginRight: 5, whiteSpace: "nowrap" }}>
                        إعادة بيع
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "center" }}><span style={{ display: "inline-block", background: "#3b82f6", color: "#fff", fontWeight: 800, fontFamily: "monospace", fontSize: 12.5, padding: "3px 9px", borderRadius: 6, minWidth: 64, letterSpacing: .3 }}>{fmt(u.effective_price)}</span></td>{/* v3.4.463 — سعر البيع في مربع أزرق بنمط صفحة الحجوزات */}
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: u.deposit_paid > 0 ? "#b45309" : "#cbd5e1" }} title={u.deposit_amount > 0 && !(u.deposit_paid > 0) ? `عربون ${fmt(u.deposit_amount)} ر.س غير مدفوع` : ""}>{u.deposit_paid > 0 ? fmt(u.deposit_paid) : (u.deposit_amount > 0 ? <span style={{ fontSize: 10.5, color: "#94a3b8", fontFamily: "inherit" }}>{fmt(u.deposit_amount)} · غير مدفوع</span> : "—")}</td>{/* v3.4.462 */}
                  <td style={{ padding: "8px 8px", textAlign: "center" }}><ExpectedAmountCell u={u} onSaved={(id, v) => setData(d => ({ ...d, units: (d.units || []).map(x => x.reservation_id === id ? { ...x, expected_payment_amount: v } : x) }))} /></td>{/* v3.4.473 */}

                  {/* v3.3.603 — تاريخ الحجز (مُعاد ترتيبه قبل موعد التقفيل) + المدة منذ الحجز */}
                  <td style={{ padding: "8px 8px", textAlign: "center", fontFamily: "monospace", color: "#64748b", fontSize: 11 }}>
                    {u.reservation_date ? String(u.reservation_date).slice(0, 10) : "—"}
                  </td>
                  {/* v3.3.604 — تظليل حسب المدة: ≤15 عادي · 15–30 برتقالي · >30 أحمر */}
                  <td style={{ padding: "8px 8px", textAlign: "center", fontSize: 11, fontWeight: 700,
                    ...(u.days_since_reservation == null ? { color: "#94a3b8", fontWeight: 600 }
                       : u.days_since_reservation > 30 ? { background: "#fee2e2", color: "#b91c1c" }
                       : u.days_since_reservation > 15 ? { background: "#ffedd5", color: "#c2410c" }
                       : { color: "#475569", fontWeight: 600 }) }}>
                    {u.days_since_reservation != null ? `${u.days_since_reservation} يوم` : "—"}
                  </td>

                  {/* Closing date+time — editable */}
                  <td style={{ padding: "8px 8px", textAlign: "center" }}>
                    {editingId === u.reservation_id ? (
                      <div style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                        <input type="date" value={editForm.closing_date}
                          onChange={e => setEditForm({ ...editForm, closing_date: e.target.value })}
                          style={{ padding: "4px 6px", fontSize: 11, borderRadius: 5, border: "1px solid #2563eb", fontFamily: "inherit" }} />
                        <input type="time" value={editForm.closing_time}
                          onChange={e => setEditForm({ ...editForm, closing_time: e.target.value })}
                          style={{ padding: "4px 6px", fontSize: 11, borderRadius: 5, border: "1px solid #2563eb", fontFamily: "inherit", width: 90 }} />
                        {/* v3.4.447 — الحفظ والإلغاء داخل خلية الموعد */}
                        <button onClick={() => saveEdit(u.reservation_id)} title="حفظ الموعد"
                          style={{ padding: "4px 8px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 11 }}>
                          <FiSave size={11} />
                        </button>
                        <button onClick={() => setEditingId(null)} title="إلغاء"
                          style={{ padding: "4px 8px", background: "#64748b", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 11 }}>
                          <FiX size={11} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <div style={{ fontFamily: "monospace", color: "#1e293b", fontWeight: 600, lineHeight: 1.3 }}>
                          <div>{u.closing_date ? u.closing_date.slice(0, 10) : "—"}</div>
                          {u.closing_time && <div style={{ fontSize: 10, color: "#2563eb", marginTop: 1 }}>🕐 {u.closing_time.slice(0, 5)}</div>}
                        </div>
                        {/* v3.4.447 — زر تعديل الموعد بجانب التاريخ (كان في عمود الإجراءات) */}
                        <button onClick={() => startEdit(u)} title={u.closing_date ? "تعديل موعد التقفيل" : "تحديد موعد التقفيل"}
                          onMouseEnter={e => { e.currentTarget.style.color = "#334155"; e.currentTarget.style.borderColor = "#94a3b8"; }}
                          onMouseLeave={e => { e.currentTarget.style.color = "#94a3b8"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
                          style={{ padding: 4, background: "transparent", color: "#94a3b8", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", lineHeight: 1, display: "inline-flex", transition: "color .15s, border-color .15s" }}>
                          <FiSettings size={12} />{/* v3.4.448 — ترس رمادي هادئ بدل الزر الأزرق */}
                        </button>
                      </div>
                    )}
                  </td>

                  <td style={{ padding: "8px 8px", textAlign: "center" }}>{closingBadge(u)}</td>

                  {/* v3.3.603 — اسم المسوق الذي جلب العميل */}
                  <td style={{ padding: "8px 8px", color: "#475569", fontSize: 12 }}>{u.marketer_name || "—"}</td>

                  {/* v3.3.373 — ملاحظات موحَّدة تراكمية */}
                  <td style={{ padding: "4px 6px", verticalAlign: "top" }}>
                    <ReservationNotesCell reservationId={u.reservation_id} notesLog={u.notes_log || []} onUpdated={load} />
                  </td>

                  {/* Actions */}
                  <td style={{ padding: "8px 4px", textAlign: "center", whiteSpace: "nowrap" }}>
                    {(
                      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>{/* v3.4.447 — زر تعديل الموعد انتقل لعمود «موعد التقفيل» */}
                        {/* v3.3.366 — زر واتساب يفتح محادثة العميل */}
                        {u.client_phone && (
                          <button onClick={() => setChatTarget({ phone: u.client_phone, name: u.client_name, clientId: u.client_id })}
                            title={`واتساب → ${u.client_name}`}
                            style={{ padding: "4px 10px", background: "#25d366", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>
                            <FiMessageCircle size={11} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* v3.3.366 — WhatsApp chat modal */}
      {chatTarget && (
        <WhatsAppChatModal
          phone={chatTarget.phone}
          name={chatTarget.name}
          session="corporate"
          onClose={() => setChatTarget(null)}
        />
      )}
    </div>
  );
}

function StatBox({ label, value, color, bg, onClick, active, sub }) { /* v3.4.477 sub */
  return (
    <div onClick={onClick} style={{
      background: bg, borderRadius: 12, padding: "14px 16px",
      border: active ? `2px solid ${color}` : "1px solid #e2e8f0",
      cursor: "pointer", transition: "all 0.15s",
    }}>
      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}
